import { Component } from '@theme/component';
import { compareStore, CompareUpdateEvent, CompareOpenEvent, CompareBlockedEvent, COMPARE_LIMIT } from '@theme/compare';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} items
 * @property {HTMLElement} count
 * @property {HTMLElement} message
 *
 * @extends {Component<Refs>}
 */
export class CompareBarElement extends Component {
  requiredRefs = ['items', 'count', 'message'];

  /** @type {ReturnType<typeof setTimeout> | null} */
  #messageTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    this.#render(compareStore.list());
    document.addEventListener(CompareUpdateEvent.eventName, this.#onUpdate);
    document.addEventListener(CompareBlockedEvent.eventName, this.#onBlocked);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CompareUpdateEvent.eventName, this.#onUpdate);
    document.removeEventListener(CompareBlockedEvent.eventName, this.#onBlocked);
  }

  /** @param {Event} event */
  #onUpdate = (event) => {
    const { items } = /** @type {CompareUpdateEvent} */ (event).detail;
    this.#render(items);
  };

  /** @param {Event} event */
  #onBlocked = (event) => {
    const { reason, existingType } = /** @type {CompareBlockedEvent} */ (event).detail;

    const text =
      reason === 'type-mismatch'
        ? `You can only compare products of the same type. Clear your current selection${
            existingType ? ` (${existingType})` : ''
          } to compare a different type.`
        : `You can compare up to ${COMPARE_LIMIT} products at a time.`;

    this.#showMessage(text);
  };

  /** @param {string} text */
  #showMessage(text) {
    if (this.#messageTimeout) clearTimeout(this.#messageTimeout);

    this.refs.message.textContent = text;
    this.refs.message.hidden = false;

    this.#messageTimeout = setTimeout(() => {
      this.refs.message.hidden = true;
      this.#messageTimeout = null;
    }, 4000);
  }

  clear() {
    compareStore.clear();
  }

  /** @param {{ id: string }} data */
  removeItem(data) {
    compareStore.remove(String(data.id));
  }

  open() {
    document.dispatchEvent(new CompareOpenEvent());
  }

  /** @param {import('@theme/compare').CompareItem[]} items */
  #render(items) {
    this.toggleAttribute('hidden', items.length === 0);
    this.refs.count.textContent = String(items.length);

    this.refs.items.replaceChildren(
      ...items.map((item) => {
        const li = document.createElement('li');
        li.className = 'compare-bar__item';

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'compare-bar__item-remove';
        remove.setAttribute('aria-label', `Remove ${item.title} from compare`);
        remove.setAttribute('on:click', `/removeItem?id=${item.id}`);
        remove.textContent = '×';

        if (item.image) {
          const img = document.createElement('img');
          img.className = 'compare-bar__item-image';
          img.src = item.image;
          img.alt = item.title;
          img.width = 40;
          img.height = 40;
          img.loading = 'lazy';
          li.append(img);
        }

        li.append(remove);
        return li;
      })
    );
  }
}

if (!customElements.get('compare-bar')) {
  customElements.define('compare-bar', CompareBarElement);
}
